// ===========================================
// Catálogo compartido de permisos: agrupación por categoría + labels amigables.
// Vive fuera de los componentes para no romper fast-refresh
// (regla react-refresh/only-export-components).
// ===========================================

import type { Permisos } from '@/types/roles'

// Agrupar permisos por categoria
export const CATEGORIAS_PERMISOS: Record<string, { label: string; permisos: (keyof Permisos)[] }> = {
  clientes: {
    label: 'Clientes',
    permisos: ['verClientes', 'crearCliente', 'editarCliente', 'verHonorariosCliente', 'editarHonorariosCliente', 'asignarResponsablesCliente', 'eliminarCliente', 'enviarEstadoCuenta'],
  },
  movimientos: {
    label: 'Movimientos',
    permisos: ['verMovimientos', 'crearMovimiento', 'editarMovimiento', 'eliminarMovimiento', 'enviarRecibo', 'generarLoteMovimientos'],
  },
  facturacion: {
    label: 'Facturación',
    permisos: ['verFacturas', 'emitirFactura', 'anularFactura'],
  },
  gastos: {
    label: 'Cuentas y Gastos Internos',
    permisos: ['verCuentas', 'crearGasto', 'editarGasto', 'eliminarGasto', 'gestionarCategoriasGasto'],
  },
  presupuestos: {
    label: 'Presupuestos',
    permisos: ['verPresupuestos', 'verPresupuestosSinCliente', 'crearPresupuesto', 'editarPresupuesto', 'eliminarPresupuesto', 'aceptarPresupuesto', 'rechazarPresupuesto', 'enviarPresupuesto'],
  },
  tareas: {
    label: 'Tareas',
    permisos: ['verTareas', 'crearTarea', 'editarTarea', 'editarTareaPropia', 'eliminarTarea'],
  },
  documentos: {
    label: 'Documentos',
    permisos: ['verDocumentos', 'subirDocumento', 'editarDocumento', 'eliminarDocumento'],
  },
  empleados: {
    label: 'Empleados',
    permisos: ['verEmpleados', 'crearEmpleado', 'editarEmpleado', 'eliminarEmpleado'],
  },
  calendario: {
    label: 'Calendario',
    permisos: ['verCalendario', 'crearVencimiento', 'editarVencimiento', 'descartarVencimiento', 'restaurarVencimiento', 'eliminarVencimiento', 'crearEvento', 'editarEvento', 'eliminarEvento', 'asignarConceptos'],
  },
  servicios: {
    label: 'Servicios',
    permisos: ['verCatalogoServicios', 'editarCatalogoServicios', 'eliminarServicio'],
  },
  impuestos: {
    label: 'Impuestos',
    permisos: ['verImpuestos', 'verCatalogoImpuestos', 'editarCatalogoImpuestos'],
  },
  configuracion: {
    label: 'Configuracion',
    permisos: ['verConfiguracion', 'editarConfiguracion'],
  },
  admin: {
    label: 'Administracion',
    permisos: ['gestionarUsuarios', 'verAuditoria', 'exportarAuditoria'],
  },
  reportes: {
    label: 'Reportes',
    permisos: ['verReportes', 'exportarReportes'],
  },
  sync: {
    label: 'Sync Vencimientos',
    permisos: ['verSyncVencimientos', 'dispararSyncManual', 'marcarRevisadoSync', 'toggleKillSwitch'],
  },
  afip: {
    label: 'AFIP / ARCA',
    permisos: ['verCasilleroCliente', 'abrirComunicacionARCA', 'sincronizarPadron', 'autoDelegarAfip', 'letritaA', 'revocarDelegacionAfip', 'verAuditAfip'],
  },
}

// Labels amigables para permisos
export const PERMISO_LABELS: Record<keyof Permisos, string> = {
  verClientes: 'Ver',
  crearCliente: 'Crear',
  editarCliente: 'Editar datos y etiquetas',
  verHonorariosCliente: 'Ver honorarios y servicios',
  editarHonorariosCliente: 'Editar honorarios y servicios',
  asignarResponsablesCliente: 'Asignar responsables',
  eliminarCliente: 'Eliminar',
  enviarEstadoCuenta: 'Enviar Estado de Cuenta',
  enviarRecibo: 'Enviar Recibo de Pago',
  verFacturas: 'Ver',
  emitirFactura: 'Emitir',
  anularFactura: 'Anular con nota de crédito',
  verMovimientos: 'Ver',
  crearMovimiento: 'Crear',
  editarMovimiento: 'Editar',
  eliminarMovimiento: 'Eliminar',
  generarLoteMovimientos: 'Generar Lote',
  verCuentas: 'Ver Cuentas',
  crearGasto: 'Crear Gasto',
  editarGasto: 'Editar Gasto',
  eliminarGasto: 'Eliminar Gasto',
  gestionarCategoriasGasto: 'Gestionar Categorías',
  verPresupuestos: 'Ver',
  verPresupuestosSinCliente: 'Ver los que no tienen cliente',
  crearPresupuesto: 'Crear',
  editarPresupuesto: 'Editar',
  eliminarPresupuesto: 'Eliminar',
  aceptarPresupuesto: 'Aceptar',
  rechazarPresupuesto: 'Rechazar',
  enviarPresupuesto: 'Enviar',
  verTareas: 'Ver',
  crearTarea: 'Crear',
  editarTarea: 'Editar',
  editarTareaPropia: 'Editar Propia',
  eliminarTarea: 'Eliminar',
  verDocumentos: 'Ver',
  subirDocumento: 'Subir',
  editarDocumento: 'Editar',
  eliminarDocumento: 'Eliminar',
  verEmpleados: 'Ver',
  crearEmpleado: 'Crear',
  editarEmpleado: 'Editar',
  eliminarEmpleado: 'Eliminar',
  verCalendario: 'Ver',
  crearVencimiento: 'Crear',
  asignarConceptos: 'Repartir conceptos entre responsables',
  editarVencimiento: 'Editar',
  descartarVencimiento: 'Descartar',
  restaurarVencimiento: 'Restaurar',
  eliminarVencimiento: 'Eliminar Vencimiento',
  crearEvento: 'Agendar Reunión',
  editarEvento: 'Editar Reunión',
  eliminarEvento: 'Eliminar Evento',
  verCatalogoServicios: 'Ver Catálogo',
  editarCatalogoServicios: 'Crear y editar',
  eliminarServicio: 'Eliminar',
  verImpuestos: 'Ver',
  verCatalogoImpuestos: 'Ver Catálogo',
  editarCatalogoImpuestos: 'Crear y editar',
  verConfiguracion: 'Ver',
  editarConfiguracion: 'Editar',
  gestionarUsuarios: 'Usuarios',
  verAuditoria: 'Ver Auditoria',
  exportarAuditoria: 'Exportar Auditoria',
  verReportes: 'Ver',
  exportarReportes: 'Exportar',
  verCasilleroCliente: 'Ver Casillero',
  abrirComunicacionARCA: 'Abrir Comunicación ARCA',
  sincronizarPadron: 'Sincronizar Padrón',
  verSyncVencimientos: 'Ver Admin',
  dispararSyncManual: 'Sync Ahora',
  marcarRevisadoSync: 'Marcar Revisado',
  toggleKillSwitch: 'Kill Switch',
  autoDelegarAfip: 'Auto-delegar AFIP',
  letritaA: 'Letrita A (acceso)',
  revocarDelegacionAfip: 'Revocar Delegación',
  verAuditAfip: 'Ver Audit AFIP',
}

// Label con su categoría, para nombrar un permiso fuera de su grupo: «Movimientos · Ver».
export const etiquetaConCategoria = (flag: keyof Permisos): string => {
  const categoria = Object.values(CATEGORIAS_PERMISOS).find((c) => c.permisos.includes(flag))
  return categoria ? `${categoria.label} · ${PERMISO_LABELS[flag]}` : PERMISO_LABELS[flag]
}
